/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, PageGridContainer } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { FooterWrapper } from 'bundles/page-footer/components/FooterWrapper';
import { CommunityLinkColumn, CourseraLinkColumn } from 'bundles/page-footer/components/global-footer/GlobalFooter';
import Copyright from 'bundles/page-footer/components/global-footer/common/Copyright';
import type { ColumnHeaderProps } from 'bundles/page-footer/components/global-footer/common/LinkColumn';
import LogoBadgeColumn from 'bundles/page-footer/components/global-footer/common/LogoBadgeColumn';
import MoreLinkColumn from 'bundles/page-footer/components/global-footer/common/MoreLinkColumn';
import type { PropsFromWithUserAgent } from 'bundles/page/common/withUserAgent';

import _t from 'i18n!nls/page';

import 'css!bundles/page-footer/components/global-footer/__styles__/GlobalFooter';

const styles = {
  bottomMobile: css`
    margin: auto;
  `,
  bottomDesktop: (theme: Theme) => css`
    padding: ${theme.spacing(16, 0, 32)};
    border-top: 1px solid ${theme.palette.gray[300]};
  `,
};

type PropsFromCaller = {
  isStickyBarPresent?: boolean;
  hideMobileAppLogoHeader?: boolean;
  hideTopBorder?: boolean;
};

type Props = PropsFromCaller & PropsFromWithUserAgent;

type DesktopPaidMediaFooterProps = { hideMobileAppLogoHeader?: boolean } & ColumnHeaderProps;

export function DesktopPaidMediaFooter({ hideMobileAppLogoHeader, columnHeader }: DesktopPaidMediaFooterProps) {
  const extraClassName = 'lohp-rebrand';
  return (
    <PageGridContainer>
      <Grid container justifyContent="center">
        <Grid item sm={3}>
          <CourseraLinkColumn columnHeader={columnHeader} extraClassName={extraClassName} />
        </Grid>
        <Grid item sm={3}>
          <CommunityLinkColumn columnHeader={columnHeader} extraClassName={extraClassName} />
        </Grid>
        <Grid item sm={3}>
          <MoreLinkColumn columnHeader={columnHeader} extraClassName={extraClassName} />
        </Grid>
        <Grid item sm={3}>
          <LogoBadgeColumn
            badgeColumn
            hideDescription
            hideLogo
            header={!hideMobileAppLogoHeader ? _t('Mobile App') : undefined}
            columnHeader={columnHeader}
            extraClassName={extraClassName}
          />
        </Grid>
        <Grid container sm={12} css={styles.bottomDesktop}>
          <Copyright />
        </Grid>
      </Grid>
    </PageGridContainer>
  );
}

const MobilePaidMediaFooter: React.FC = () => {
  const extraClassName = 'lohp-rebrand';
  return (
    <PageGridContainer>
      <Grid container justifyContent="space-around">
        <Grid item sm={6}>
          <CourseraLinkColumn extraClassName={extraClassName} />
        </Grid>
        <Grid item sm={6}>
          <CommunityLinkColumn extraClassName={extraClassName} />
        </Grid>
        <Grid item sm={12}>
          <MoreLinkColumn extraClassName={extraClassName} />
        </Grid>
        <LogoBadgeColumn
          extraClassName={extraClassName}
          hideDescription
          hideLogo
          isMobile
          header={_t('Learn Anywhere')}
        />
        <Grid css={styles.bottomMobile}>
          <Copyright marginTop marginBottom />
        </Grid>
      </Grid>
    </PageGridContainer>
  );
};

export const PaidMediaFooter: React.FC<Props> = ({
  isStickyBarPresent,
  hideMobileAppLogoHeader = false,
  hideTopBorder,
}) => {
  const classNamesFooter = `rc-GlobalFooter rc-PageFooter rc-PaidMediaFooter ${
    isStickyBarPresent ? 'sticky-bar' : ''
  } ${hideTopBorder ? 'hide-top-border' : ''} lohp-rebrand`;

  return (
    <FooterWrapper
      footerComponentMobile={<MobilePaidMediaFooter />}
      footerComponentDesktop={
        <DesktopPaidMediaFooter hideMobileAppLogoHeader={hideMobileAppLogoHeader} columnHeader="h3" />
      }
      extraClassNames={classNamesFooter}
    />
  );
};

export default PaidMediaFooter;
